import React, { useContext, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  IconButton,
  Typography,
  makeStyles,
  Button,
  CircularProgress,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { DataContext } from 'contexts/DataContext';
import { List, RotatorItem, rotateStatus } from 'dataProvider';

const useStyles = makeStyles({
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingRight: 8,
  },
  closeButton: {
    marginLeft: 'auto',
  },
  entryCard: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    backgroundColor: '#fafafa',
  },
  entryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  statusBadge: {
    padding: '4px 12px',
    borderRadius: 16,
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  statusAdded: {
    backgroundColor: '#c8e6c9',
    color: '#2e7d32',
  },
  statusSelect: {
    backgroundColor: '#fff3e0',
    color: '#ef6c00',
  },
  statusPay: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
  },
  statusPending: {
    backgroundColor: '#f3e5f5',
    color: '#7b1fa2',
  },
  entryDetails: {
    color: '#666',
    fontSize: 14,
  },
  actionButton: {
    marginTop: 8,
  },
  cancelButton: {
    backgroundColor: '#dc3545',
    color: 'white',
    '&:hover': {
      backgroundColor: '#c82333',
    },
  },
  continueButton: {
    backgroundColor: '#28a745',
    color: 'white',
    marginRight: 8,
    '&:hover': {
      backgroundColor: '#218838',
    },
  },
  stayButton: {
    color: '#6c757d',
    '&:hover': {
      backgroundColor: 'rgba(108, 117, 125, 0.1)',
    },
  },
  exitButton: {
    backgroundColor: '#dc3545',
    color: 'white',
    '&:hover': {
      backgroundColor: '#c82333',
    },
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: 40,
  },
  noEntries: {
    textAlign: 'center' as const,
    padding: 40,
    color: '#666',
  },
});

interface MyEntriesModalProps {
  open: boolean;
  onClose: () => void;
  list: List;
  onContinueEntry: (entryId: string | number) => void;
  onCancelEntry: (entryId: string | number) => void;
}

const getStatusLabel = (status: string): string => {
  switch (status) {
    case rotateStatus.ADDED:
      return 'Active';
    case rotateStatus.SELECT:
      return 'Selecting';
    case rotateStatus.PAY:
      return 'Payment Pending';
    case rotateStatus.PENDING:
      return 'Pending';
    default:
      return status;
  }
};

const getStatusClass = (
  status: string,
  classes: ReturnType<typeof useStyles>,
): string => {
  switch (status) {
    case rotateStatus.ADDED:
      return classes.statusAdded;
    case rotateStatus.SELECT:
      return classes.statusSelect;
    case rotateStatus.PAY:
      return classes.statusPay;
    case rotateStatus.PENDING:
      return classes.statusPending;
    default:
      return '';
  }
};

export const MyEntriesModal: React.FC<MyEntriesModalProps> = ({
  open,
  onClose,
  list,
  onContinueEntry,
  onCancelEntry,
}) => {
  const classes = useStyles();
  const { dataProvider } = useContext(DataContext);
  const [entries, setEntries] = useState<Array<RotatorItem>>([]);
  const [loading, setLoading] = useState(true);
  const [cancelConfirmEntryId, setCancelConfirmEntryId] = useState<
    string | number | null
  >(null);

  useEffect(() => {
    if (open && list?.id) {
      setLoading(true);
      dataProvider
        .getMyEntriesForList(list.id)
        .then((data) => {
          setEntries(data || []);
        })
        .catch((err) => {
          console.error(err);
          setEntries([]);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, list?.id, dataProvider]);

  const formatDate = (dateStr: string | Date | undefined): string => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleCancelClick = (entryId: string | number) => {
    setCancelConfirmEntryId(entryId);
  };

  const handleCancelConfirm = () => {
    if (cancelConfirmEntryId !== null) {
      onCancelEntry(cancelConfirmEntryId);
      setCancelConfirmEntryId(null);
    }
  };

  const handleCancelDismiss = () => {
    setCancelConfirmEntryId(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography variant="h6">
            My Entries - {list?.name} Money List
          </Typography>
          <IconButton className={classes.closeButton} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <div className={classes.loading}>
              <CircularProgress />
            </div>
          ) : entries.length === 0 ? (
            <div className={classes.noEntries}>
              <Typography>You have no active entries on this list.</Typography>
            </div>
          ) : (
            entries.map((entry, index) => (
              <div key={entry.id} className={classes.entryCard}>
                <div className={classes.entryHeader}>
                  <Typography variant="subtitle1">
                    Entry #{index + 1}
                  </Typography>
                  <span
                    className={`${classes.statusBadge} ${getStatusClass(
                      entry.status,
                      classes,
                    )}`}
                  >
                    {getStatusLabel(entry.status)}
                  </span>
                </div>
                <div className={classes.entryDetails}>
                  <Typography variant="body2">
                    Created: {formatDate(entry.createdAt)}
                  </Typography>
                </div>
                <div className={classes.actionButton}>
                  {entry.status === rotateStatus.SELECT && (
                    <Button
                      variant="contained"
                      size="small"
                      className={classes.continueButton}
                      onClick={() => onContinueEntry(entry.id)}
                    >
                      Set as active list
                    </Button>
                  )}
                  {entry.status !== rotateStatus.ADDED && (
                    <Button
                      variant="contained"
                      size="small"
                      className={classes.cancelButton}
                      onClick={() => handleCancelClick(entry.id)}
                    >
                      Cancel Entry
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={cancelConfirmEntryId !== null}
        onClose={handleCancelDismiss}
        aria-labelledby="cancel-entry-dialog-title"
        aria-describedby="cancel-entry-dialog-description"
      >
        <DialogTitle id="cancel-entry-dialog-title">Cancel Entry</DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-entry-dialog-description">
            You will not be refunded for any payments you've made
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelDismiss}
            className={classes.stayButton}
            autoFocus
          >
            Stay in List
          </Button>
          <Button
            onClick={handleCancelConfirm}
            className={classes.exitButton}
            variant="contained"
          >
            Exit List
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MyEntriesModal;
